/**
 * BehaviorGraph — FlowGraph-based interactivity behavior.
 *
 * Two runtime modes:
 * 1. **Compiled mode** (default): Compiles serialized GraphData into live
 *    FlowGraph blocks on play mode entry via GraphCompiler. Used for
 *    user-authored graphs in the editor.
 *
 * 2. **KHR-managed mode**: When the graph came from a loaded GLB's
 *    KHR_interactivity extension, Babylon's loader already compiled it
 *    (with our registered trace blocks). The BehaviorGraph stores the data
 *    for editor visibility but delegates runtime to Babylon's coordinator.
 *    Set `khrManaged = true` after attaching.
 */

import { TraceBehavior } from "../tracebehavior";
import type { ValidationError } from "../../scenedata/behaviordata";
import type { BehaviorGraphData } from "../../scenedata/behaviors/behaviorgraphdata";
import type { SceneObject } from "../../core/SceneObject";
import { compileGraph, type CompiledGraph } from "./GraphCompiler";
import { lowerBehaviorGraphData, lowerMacros } from "./macros";
import { FlowGraphCoordinator } from "@babylonjs/core/FlowGraph/flowGraphCoordinator";
import { setTraceContext } from "./blocks/traceContext";
import { Logger } from "../../utils/Logger";
import { validateExecutableGraph, GraphValidationError, type GraphDiagnostic } from './graphValidation';

const NO_SELECT_TARGETS: ReadonlySet<SceneObject> = new Set();

export class BehaviorGraph extends TraceBehavior<BehaviorGraphData> {
    static readonly behaviorType = "BehaviorGraph" as const;

    constructor(sceneObject: SceneObject, data: BehaviorGraphData) {
        // Data-ingestion choke point #1 (initial construction): lower any
        // macro pseudo-nodes (`macro/interval`, …) before the base class
        // stores authored data — stored GraphData only ever contains standard
        // ops. No-op (and reference-preserving) on already-lowered data.
        super(sceneObject, lowerBehaviorGraphData(data));
    }

    private _compiled: CompiledGraph | null = null;

    /**
     * Serializable diagnostics for inspectors and authoring tools. Data is never discarded.
     * A KHR-managed graph executes in Babylon's coordinator, not the native compiler, so
     * it reports nothing here — the same short-circuit `validate()` applies. Without it an
     * imported graph with a templated pointer would read as broken while running correctly.
     */
    public getRuntimeDiagnostics(): GraphDiagnostic[] {
        if (this.khrManaged) return [];
        const data = this._resolved;
        const graphs = data.graphs ?? (data.graph ? [data.graph] : []);
        if (!graphs.length) return [];
        const active = data.activeGraph ?? 0;
        if (!Number.isInteger(active) || !graphs[active]) return [{ code: 'ACTIVE_GRAPH', field: 'activeGraph', message: `Invalid active graph index ${active}.` }];
        return validateExecutableGraph(graphs[active], graphs);
    }

    /**
     * When true, the graph runtime is managed by Babylon's KHR_interactivity
     * loader — don't compile a second coordinator. The BehaviorGraph still
     * stores the data for the editor and routes pointer events.
     */
    public khrManaged = false;

    /** Babylon's coordinator (only set in KHR-managed mode) */
    private _khrCoordinator: FlowGraphCoordinator | null = null;

    protected override onPlayModeChanged(playing: boolean): void {
        if (playing) {
            try {
                if (this.khrManaged) this._attachToKHRCoordinator();
                else this._buildAndStart();
            } catch (error) {
                Logger.warn(`[BehaviorGraph] Graph did not start: ${error instanceof Error ? error.message : String(error)}`);
            }
        } else {
            this._teardown();
        }
    }

    // No onPointerEvent override needed — Babylon's native event blocks
    // (FlowGraphMeshPickEventBlock, FlowGraphPointerOverEventBlock,
    // FlowGraphPointerOutEventBlock) listen to scene pointer observables
    // directly via FlowGraphSceneEventCoordinator.

    /**
     * The objects whose taps (on them or anything under them) fire this
     * graph's onSelect events right now — see `CompiledGraph.selectTargets`.
     * Empty when this behavior compiled nothing: edit mode, an empty graph,
     * one that failed to start, or a `khrManaged` graph. Babylon's glTF
     * loader runs that one and resolves its pick targets only while
     * executing, so it exposes none; TraceScene treats its host as
     * listening on its whole subtree.
     */
    public selectTargets(): ReadonlySet<SceneObject> {
        return this._compiled?.selectTargets ?? NO_SELECT_TARGETS;
    }

    /**
     * Data-ingestion choke point #2 (commit/commitSnapshot): migration-style
     * in-place lowering of macro pseudo-nodes. High-level authoring (the AI
     * assistant emits graph JSON with e.g. a single `macro/interval` node)
     * commits through here, so authored/stored data only ever contains the
     * standard-op expansion. Idempotent — already-lowered graphs pass
     * through untouched.
     */
    protected override validate(data: BehaviorGraphData): ValidationError[] {
        if (data.graph) data.graph = lowerMacros(data.graph);
        if (data.graphs) data.graphs = data.graphs.map(g => lowerMacros(g));
        // Paused editor/import data may contain unknown extensions. Keep it
        // round-trippable, but reject invalid hot edits before replacing a live graph.
        const graphs = data.graphs ?? (data.graph ? [data.graph] : []);
        if (!this.playMode || this.khrManaged || !graphs.length) return [];
        const active = data.activeGraph ?? 0;
        if (!Number.isInteger(active) || !graphs[active]) return [{ code: 'ACTIVE_GRAPH', field: 'activeGraph', message: `Invalid active graph index ${active}.` }];
        return validateExecutableGraph(graphs[active], graphs);
    }

    protected override async applyCommit(): Promise<void> {
        // Compile before replacing the live graph.
        if (this.playMode && !this.khrManaged) {
            this._buildAndStart();
        }
    }

    protected override onDetach(): void {
        this._teardown();
    }

    /**
     * KHR-managed mode: find Babylon's coordinator and stash trace context
     * on its execution contexts so our blocks can resolve SceneObjects.
     */
    private _attachToKHRCoordinator(): void {
        const scene = this.sceneObject.babylonScene;
        const coordinators = FlowGraphCoordinator.SceneCoordinators.get(scene);
        if (!coordinators || coordinators.length === 0) {
            Logger.warn("[BehaviorGraph] KHR-managed but no coordinator found — falling back to compiled mode");
            this._buildAndStart();
            return;
        }

        // Use the first coordinator (created by Babylon's KHR_interactivity loader)
        this._khrCoordinator = coordinators[0];

        // Stash trace context on all execution contexts
        for (const graph of this._khrCoordinator.flowGraphs) {
            for (let i = 0; ; i++) {
                const ctx = graph.getContext(i);
                if (!ctx) break;
                setTraceContext(ctx, this.sceneObject.traceScene, this.sceneObject);
            }
        }
    }

    /** Compiled mode: compile GraphData into a new coordinator. */
    private _buildAndStart(): void {
        const diagnostics = this.getRuntimeDiagnostics();
        if (diagnostics.length) throw new GraphValidationError(diagnostics);
        const data = this._resolved;
        const allGraphs = data.graphs ?? (data.graph ? [data.graph] : []);
        const activeIndex = data.activeGraph ?? 0;
        const graphData = allGraphs[activeIndex];

        if (!graphData || graphData.nodes.length === 0) {
            this._teardown();
            Logger.log("[BehaviorGraph] Empty graph, nothing to run");
            return;
        }

        const compiled = compileGraph(
            graphData,
            this.sceneObject.babylonScene,
            this.sceneObject.traceScene,
            this.sceneObject,
            allGraphs.length > 1 ? allGraphs : undefined,
        );

        this._teardown();
        this._compiled = compiled;
        compiled.coordinator.start();

        // `event/onStart` runs once the scene has stopped being built, not
        // inside the construction of the one object this graph happens to sit
        // on. A scene assembled object-by-object (createSceneObjectFromData in
        // a loop) attaches — and, in play mode, starts — each graph before the
        // later objects exist, so a start-time action targeting a sibling
        // authored further down the file would resolve to nothing. See
        // TraceScene._deferStartEvent.
        //
        // The optional call is deliberate: lightweight test doubles stand in
        // for TraceScene, and firing inline is the right fallback for them.
        const traceScene = this.sceneObject.traceScene;
        const fireStart = () => {
            // Only if this compilation is still the live one — a teardown or a
            // hot re-compile in between makes the pending event stale.
            if (this._compiled === compiled) compiled.fireEvent('trace:start');
        };
        if (traceScene?._deferStartEvent) traceScene._deferStartEvent(fireStart);
        else fireStart();
    }

    private _teardown(): void {
        if (this._compiled) {
            this._compiled.dispose();
            this._compiled = null;
        }
        this._khrCoordinator = null;
    }
}
